import {
  get,
  put,
  post,
  internalDestroy,
  internalGet,
  internalPost,
} from './index';
import qs from 'qs';
import { HotelContractStatusEnum, ContractStateFilterEnum } from '@koob/enums';

export const getHotelsContracts = ({ hotelId }) => {
  return get(`hotels/${hotelId}/contractsAvailable`);
};

export const createContract = ({ hotelId, data }) => {
  return post(`hotels/${hotelId}/contracts`, data);
};

// GET returns the contract's legacy fields (status, hotel,
// hotelRoomContracts[].periods for Night rates) plus the season model,
// `currency` and `validation`.
export const getContract = ({ hotelId, contractId }) => {
  return get(`hotels/${hotelId}/contracts/${contractId}`);
};

export const SEASON_MODEL_KEYS = [
  'settings',
  'ageGroups',
  'seasons',
  'rooms',
  'seasonRooms',
];

export const pickSeasonModel = (contract) =>
  Object.fromEntries(SEASON_MODEL_KEYS.map((key) => [key, contract?.[key]]));

// PUT replaces the whole season model (validity dates live in settings).
// The response is the saved model with `currency` and `validation`.
export const updateContract = ({ hotelId, contractId, model }) => {
  return put(
    `hotels/${hotelId}/contracts/${contractId}`,
    pickSeasonModel(model),
  );
};

// The {errors, warnings} a PUT of `model` would answer; writes nothing.
export const checkContract = ({ hotelId, contractId, model }) => {
  return post(
    `hotels/${hotelId}/contracts/${contractId}/validation`,
    pickSeasonModel(model),
  );
};

export const deleteContract = ({ hotelId, contractId }) => {
  return internalDestroy(`hotels/${hotelId}/contracts/${contractId}`);
};

export const getHotelContracts = ({ hotelId, filters, skip = false }) => {
  if (skip) return Promise.resolve({ data: [] });

  const queryString = qs.stringify(filters);
  return get(`hotels/${hotelId}/contracts?${queryString}`);
};

export const publishContract = ({ hotelId, contractId, data }) => {
  return put(`hotels/${hotelId}/contracts/${contractId}/publish`, data);
};

export const getStopSalesCarryOver = ({ hotelId, contractId }) => {
  return get(`hotels/${hotelId}/contracts/${contractId}/stop-sales-carry-over`);
};

export const duplicateContract = ({ hotelId, contractId }) => {
  return post(`hotels/${hotelId}/contracts/${contractId}/duplicate`);
};

// Every currently-available PUBLISHED contract of a hotel, each fetched in full
// (rooms + periods). Used by the hotel-level Night rates editor, which needs the
// period grid to overlay per-night overrides on top of. Returns `{ data: [...] }`
// so it plugs into react-async like the other endpoints.
export const getHotelActiveContractsDetailed = async ({ hotelId }) => {
  const listResponse = await getHotelContracts({
    hotelId,
    filters: {
      status: HotelContractStatusEnum.PUBLISHED,
      contractStateFilter: ContractStateFilterEnum.AVAILABLE,
      perPage: 200,
    },
  });
  const summaries = listResponse?.data ?? [];

  const results = await Promise.allSettled(
    summaries.map(({ id }) => getContract({ hotelId, contractId: id })),
  );

  const data = results
    .filter((r) => r.status === 'fulfilled' && r.value?.data)
    .map((r) => r.value.data);

  return { data };
};

// Contract workbook (internal API): export, then upload → review → confirm.
export const downloadContractWorkbook = ({ hotelId, contractId }) =>
  internalGet(
    `hotels/${hotelId}/contracts/${contractId}/workbook`,
    { responseType: 'blob' },
    false,
  );

export const uploadContractWorkbook = ({ hotelId, contractId, file }) => {
  const form = new FormData();
  form.append('file', file);
  return internalPost(
    `hotels/${hotelId}/contracts/${contractId}/imports`,
    form,
  );
};

export const getContractImport = ({ importId, page, perPage }) =>
  internalGet(
    `hotel-contract-imports/${importId}?page=${page}&perPage=${perPage}`,
  );

export const confirmContractImport = ({ importId, reviewToken }) =>
  internalPost(`hotel-contract-imports/${importId}/confirmation`, {
    confirmed: true,
    reviewToken,
  });
